import axiosInstance from "../../hooks/axiosInstance";
import useAuth from "../../hooks/useAuthContext";
import { useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";

const styles = `
  .icon-communicative {
    transition: all 0.3s ease-in-out;
    cursor: help;
    position: relative;
  }
  
  .icon-communicative:hover {
    transform: scale(1.2);
    filter: brightness(1.2);
  }
  
  .icon-communicative::after {
    content: attr(title);
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    background-color: #333;
    color: white;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
    white-space: nowrap;
    opacity: 0;
    visibility: hidden;
    transition: all 0.2s ease;
    z-index: 1000;
  }
  
  .icon-communicative:hover::after {
    opacity: 1;
    visibility: visible;
    bottom: calc(100% + 5px);
  }
  
  .analytics-card {
    border-radius: 8px;
    box-shadow: 0 0.75rem 1.5rem rgba(0, 0, 0, 0.03);
    transition: all 0.3s ease;
    height: 100%;
  }
  
  .analytics-card:hover {
    transform: translateY(-5px);
    box-shadow: 0 1rem 2rem rgba(0, 0, 0, 0.05);
  }
  
  .status-badge {
    padding: 4px 8px;
    border-radius: 12px;
    font-size: 0.75rem;
    font-weight: 500;
    cursor: pointer;
  }
  
  .status-badge:hover {
    opacity: 0.8;
  }
  
  .payment-paid {
    background-color: #d4edda;
    color: #155724;
  }
  
  .payment-unpaid {
    background-color: #f8d7da;
    color: #721c24;
  }
  
  .payment-partial {
    background-color: #fff3cd;
    color: #856404;
  }
  
  .order-status-pending {
    background-color: #fff3cd;
    color: #856404;
  }
  
  .order-status-approved {
    background-color: #d4edda;
    color: #155724;
  }
  
  .order-status-reject {
    background-color: #f8d7da;
    color: #721c24;
  }
  
  .requisition-status-pending {
    background-color: #fff3cd;
    color: #856404;
  }
  
  .requisition-status-finance_approved {
    background-color: #cce7ff;
    color: #004085;
  }
  
  .requisition-status-chain_supply_approved {
    background-color: #d4edda;
    color: #155724;
  }
  
  .requisition-status-rejected {
    background-color: #f8d7da;
    color: #721c24;
  }
`;

export default function DashboardPage() {
  const { userData } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalData, setModalData] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);

  // Fetch dashboard analytics data
  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const response = await axiosInstance.get(
          "/user/dashboard/analytics",
          {}
        );

 
          setAnalytics(response.data.data.analytics);
      } catch (error) {
        console.error("Error fetching dashboard analytics:", error);
      } finally {
        setLoading(false);
      }
    };

    if (userData) {
      fetchAnalytics();
    }
  }, [userData]);

  // Function to fetch orders by status
  const fetchOrdersByStatus = async (statusType, statusValue) => {
    setModalLoading(true);
    setModalTitle(`${statusType}: ${statusValue}`);

    try {
      let response;

      if (statusType === "Order Status") {
        // Fetch orders by order status
        response = await axiosInstance.get(
          `/user/orders?status=${statusValue}`
        );
      } else if (statusType === "Requisition Status") {
        // Fetch orders by requisition status
        response = await axiosInstance.get(
          `/user/requisitions?status=${statusValue}`
        );
      }

  
        setModalData(response.data.data.orders || []);
    } catch (error) {
      console.error("Error fetching orders:", error);
      setModalData([]);
    } finally {
      setModalLoading(false);
      setShowModal(true);
    }
  };

  // Check if userData and its properties exist
  if (!userData || !userData.userInfo || !userData.userInfo.user) {
    return <div>Loading...</div>; // Or some other fallback UI
  }

  // Show loading state while fetching analytics
  if (loading) {
    return <div>Loading dashboard data...</div>;
  }

  // Correctly declare and initialize user variable
  const user = userData.userInfo.user;

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: "NGN",
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  // Handle closing the modal
  const handleCloseModal = () => {
    setShowModal(false);
    setModalData([]);
  };

  return (
    <>
      <style>{styles}</style>
      <div className="row">
        <div className="col">
          <div className="h-100">
            <div className="row mb-3 pb-1">
              <div className="col-12">
                <div className="d-flex align-items-lg-center flex-lg-row flex-column">
                  <div className="flex-grow-1">
                    <h4 className="fs-16 mb-1">
                      Welcome back,{" "}
                      <span className="text-info font-weight-bold">
                        {user?.first_name} {user?.last_name || "User"}
                      </span>
                    </h4>
                    <p className="text-muted mb-0">
                      Here’s what’s happening within Justfood today.
                    </p>
                  </div>
                </div>
                {/* <!-- end card header --> */}
              </div>
              {/* <!--end col--> */}
            </div>
            {/* <!--end row--> */}

            <div className="row">
              <div className="col-xl-12">
                <div className="card crm-widget">
                  <div className="card-body p-0">
                    <div className="row row-cols-xxl-5 row-cols-md-4 row-cols-1 g-0">
                      <div className="col">
                        <div className="py-4 px-3">
                          <h5 className="text-muted text-uppercase fs-13">
                            Total Orders (Month){" "}
                            <i className="ri-shopping-cart-2-line text-success fs-18 float-end align-middle"></i>
                          </h5>
                          <div className="d-flex align-items-center">
                            <div className="flex-shrink-0">
                              <i
                                className="ri-shopping-cart-2-line display-6 text-primary icon-communicative"
                                title="Total orders this month"
                              ></i>
                            </div>
                            <div className="flex-grow-1 ms-3">
                              <h2 className="mb-0">
                                <span className="counter-value" data-target="5">
                                  {analytics?.total_orders_month || 0}
                                </span>
                              </h2>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col">
                        <div className="mt-3 mt-md-0 py-4 px-3">
                          <h5 className="text-muted text-uppercase fs-13">
                            Sales Today{" "}
                            <i className="ri-arrow-up-circle-line text-success fs-18 float-end align-middle"></i>
                          </h5>
                          <div className="d-flex align-items-center">
                            <div className="flex-shrink-0">
                              <i
                                className="ri-cash-line display-6 text-success icon-communicative"
                                title="Sales transactions today"
                              ></i>
                            </div>
                            <div className="flex-grow-1 ms-3">
                              <h2 className="mb-0">
                                <span className="counter-value" data-target="8">
                                  {analytics?.total_sales_today || 0}
                                </span>
                              </h2>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col">
                        <div className="mt-3 mt-md-0 py-4 px-3">
                          <h5 className="text-muted text-uppercase fs-13">
                            Sales (Month){" "}
                            <i className="ri-arrow-down-circle-line text-warning fs-18 float-end align-middle"></i>
                          </h5>
                          <div className="d-flex align-items-center">
                            <div className="flex-shrink-0">
                              <i
                                className="ri-money-dollar-circle-line display-6 text-info icon-communicative"
                                title="Total sales this month"
                              ></i>
                            </div>
                            <div className="flex-grow-1 ms-3">
                              <h2 className="mb-0">
                                <span className="counter-value" data-target="7">
                                  {analytics?.total_sales_month || 0}
                                </span>
                              </h2>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col">
                        <div className="mt-3 mt-lg-0 py-4 px-3">
                          <h5 className="text-muted text-uppercase fs-13">
                            Total Revenue{" "}
                            <i className="ri-arrow-up-circle-line text-success fs-18 float-end align-middle"></i>
                          </h5>
                          <div className="d-flex align-items-center">
                            <div className="flex-shrink-0">
                              <i
                                className="ri-money-cny-circle-line display-6 text-warning icon-communicative"
                                title="Total revenue this month"
                              ></i>
                            </div>
                            <div className="flex-grow-1 ms-3">
                              <h2 className="mb-0">
                                <span
                                  className="counter-value"
                                  data-target="15"
                                >
                                  {formatCurrency(analytics?.total_revenue)}
                                </span>
                              </h2>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col">
                        <div className="mt-3 mt-lg-0 py-4 px-3">
                          <h5 className="text-muted text-uppercase fs-13">
                            Products Sold{" "}
                            <i className="ri-arrow-down-circle-line text-danger fs-18 float-end align-middle"></i>
                          </h5>
                          <div className="d-flex align-items-center">
                            <div className="flex-shrink-0">
                              <i
                                className="ri-box-3-line display-6 text-success icon-communicative"
                                title="Total units sold"
                              ></i>
                            </div>
                            <div className="flex-grow-1 ms-3">
                              <h2 className="mb-0">
                                <span
                                  className="counter-value"
                                  data-target="1050"
                                >
                                  {analytics?.total_products_sold || 0}
                                </span>
                              </h2>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col">
                        <div className="mt-3 mt-lg-0 py-4 px-3">
                          <h5 className="text-muted text-uppercase fs-13">
                            Top Selling Product{" "}
                            <i className="ri-arrow-down-circle-line text-danger fs-18 float-end align-middle"></i>
                          </h5>
                          <div className="d-flex align-items-center">
                            <div className="flex-shrink-0">
                              <i
                                className="ri-trophy-line display-6 text-danger icon-communicative"
                                title="Best performing product"
                              ></i>
                            </div>
                            <div className="flex-grow-1 ms-3">
                              <h2 className="mb-0">
                                <span
                                  className="counter-value"
                                  style={{
                                    fontSize: "20px",
                                    lineHeight: "2px",
                                  }}
                                  data-target="1050"
                                >
                                  {analytics?.top_selling_product?.name ||
                                    "N/A"}
                                </span>
                              </h2>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col">
                        <div className="mt-3 mt-lg-0 py-4 px-3">
                          <h5 className="text-muted text-uppercase fs-13">
                            Top Customer{" "}
                            <i className="ri-arrow-down-circle-line text-danger fs-18 float-end align-middle"></i>
                          </h5>
                          <div className="d-flex align-items-center">
                            <div className="flex-shrink-0">
                              <i
                                className="ri-customer-service-line display-6 text-primary icon-communicative"
                                title="Most valuable customer"
                              ></i>
                            </div>
                            <div className="flex-grow-1 ms-3">
                              <h2 className="mb-0">
                                <span
                                  className="counter-value"
                                  style={{
                                    fontSize: "20px",
                                    lineHeight: "2px",
                                  }}
                                  data-target="1050"
                                >
                                  {analytics?.top_buying_customer?.name ||
                                    "N/A"}
                                </span>
                              </h2>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col">
                        <div className="mt-3 mt-lg-0 py-4 px-3">
                          <h5 className="text-muted text-uppercase fs-13">
                            Top Outlet{" "}
                            <i className="ri-arrow-down-circle-line text-danger fs-18 float-end align-middle"></i>
                          </h5>
                          <div className="d-flex align-items-center">
                            <div className="flex-shrink-0">
                              <i
                                className="ri-store-2-line display-6 text-info icon-communicative"
                                title="Highest performing outlet (warehouse)"
                              ></i>
                            </div>
                            <div className="flex-grow-1 ms-3">
                              <h2 className="mb-0">
                                <span
                                  className="counter-value"
                                  style={{
                                    fontSize: "20px",
                                    lineHeight: "2px",
                                  }}
                                  data-target="1050"
                                >
                                  {analytics?.top_outlet?.name || "N/A"}
                                </span>
                              </h2>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* <!-- Payment Status Summary --> */}
            <div className="row">
              <div className="col-xl-12">
                <div className="card analytics-card">
                  <div className="card-header">
                    <h5 className="card-title mb-0">Payment Status Summary</h5>
                  </div>
                  <div className="card-body">
                    <div className="table-responsive">
                      <table className="table table-centered table-nowrap mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Status</th>
                            <th>Count</th>
                            <th>Total Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {analytics?.payment_status_summary &&
                          analytics.payment_status_summary.length > 0 ? (
                            analytics.payment_status_summary.map(
                              (payment, index) => (
                                <tr key={index}>
                                  <td>
                                    <span
                                      className={`status-badge payment-${
                                        payment.status === "paid"
                                          ? "paid"
                                          : payment.status === "unpaid"
                                          ? "unpaid"
                                          : "partial"
                                      }`}
                                    >
                                      {payment.status}
                                    </span>
                                  </td>
                                  <td>{payment.count}</td>
                                  <td>
                                    {formatCurrency(payment.total_amount)}
                                  </td>
                                </tr>
                              )
                            )
                          ) : (
                            <tr>
                              <td colSpan="3" className="text-center">
                                No payment data available
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* <!-- Top Products by Quantity --> */}
            <div className="row">
              <div className="col-xl-6">
                <div className="card analytics-card">
                  <div className="card-header">
                    <h5 className="card-title mb-0">
                      Top Products by Quantity Sold
                    </h5>
                  </div>
                  <div className="card-body">
                    <div className="table-responsive">
                      <table className="table table-centered table-nowrap mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Product Name</th>
                            <th>Quantity Sold</th>
                          </tr>
                        </thead>
                        <tbody>
                          {analytics?.top_products_by_quantity &&
                          analytics.top_products_by_quantity.length > 0 ? (
                            analytics.top_products_by_quantity.map(
                              (product, index) => (
                                <tr key={index}>
                                  <td>{product.product_name}</td>
                                  <td>{product.total_quantity}</td>
                                </tr>
                              )
                            )
                          ) : (
                            <tr>
                              <td colSpan="2" className="text-center">
                                No product data available
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>

              {/* <!-- Recent Transfers --> */}
              <div className="col-xl-6">
                <div className="card analytics-card">
                  <div className="card-header">
                    <h5 className="card-title mb-0">Recent Transfers</h5>
                  </div>
                  <div className="card-body">
                    <div className="table-responsive">
                      <table className="table table-centered table-nowrap mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>From Warehouse</th>
                            <th>Product</th>
                            <th>Quantity</th>
                            <th>Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {analytics?.recent_transfers &&
                          analytics.recent_transfers.length > 0 ? (
                            analytics.recent_transfers.map(
                              (transfer, index) => (
                                <tr key={index}>
                                  <td>{transfer.ware_house?.name || "N/A"}</td>
                                  <td>
                                    {transfer.product?.product_name || "N/A"}
                                  </td>
                                  <td>{transfer.quantity}</td>
                                  <td>
                                    {new Date(
                                      transfer.created_at
                                    ).toLocaleDateString()}
                                  </td>
                                </tr>
                              )
                            )
                          ) : (
                            <tr>
                              <td colSpan="4" className="text-center">
                                No transfer data available
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* <!-- Orders by Status --> */}
            <div className="row">
              <div className="col-xl-12">
                <div className="card analytics-card">
                  <div className="card-header">
                    <h5 className="card-title mb-0">Orders by Status</h5>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      {/* Order Status Section */}
                      <div className="col-md-6">
                        <h6>Order Status (Sales Manager)</h6>
                        <div className="d-flex flex-wrap gap-2">
                          {analytics?.orders_by_status ? (
                            Object.entries(analytics.orders_by_status).map(
                              ([status, count]) => (
                                <div
                                  key={status}
                                  className={`border rounded p-3 text-center status-badge order-status-${status}`}
                                  onClick={() =>
                                    fetchOrdersByStatus("Order Status", status)
                                  }
                                >
                                  <h5 className="mb-1">{count}</h5>
                                  <p className="text-muted mb-0 text-capitalize">
                                    {status}
                                  </p>
                                </div>
                              )
                            )
                          ) : (
                            <p className="text-muted">
                              No order status data available
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Requisition Status Section */}
                      <div className="col-md-6">
                        <h6>Requisition Status (Approval Workflow)</h6>
                        <div className="d-flex flex-wrap gap-2">
                          {analytics?.requisition_status_summary ? (
                            analytics.requisition_status_summary.map(
                              (req, index) => (
                                <div
                                  key={index}
                                  className={`border rounded p-3 text-center status-badge requisition-status-${req.status}`}
                                  onClick={() =>
                                    fetchOrdersByStatus(
                                      "Requisition Status",
                                      req.status
                                    )
                                  }
                                >
                                  <h5 className="mb-1">{req.count}</h5>
                                  <p className="text-muted mb-0 text-capitalize">
                                    {req.status.replace("_", " ")}
                                  </p>
                                </div>
                              )
                            )
                          ) : (
                            <p className="text-muted">
                              No requisition status data available
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* <!-- Orders Modal --> */}
      <Modal show={showModal} onHide={handleCloseModal} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>{modalTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalLoading ? (
            <div className="text-center">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : modalData.length > 0 ? (
            <div className="table-responsive">
              <table className="table table-centered table-nowrap mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Order Code</th>
                    <th>Customer/Company</th>
                    <th>Product</th>
                    {/* Only show Quantity and Total Price for non-pending requisitions */}
                    {modalTitle !== "Requisition Status: pending" && (
                      <>
                        <th>Quantity</th>
                        <th>Total Price</th>
                      </>
                    )}
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {modalData.map((order, index) => (
                    <tr key={index}>
                      <td>{order.order_code}</td>
                      <td>
                        {order.company_name ||
                          `${order.first_name} ${order.last_name}`}
                      </td>
                      <td>{order.product_name}</td>
                      {/* Only show Quantity and Total Price for non-pending requisitions */}
                      {modalTitle !== "Requisition Status: pending" && (
                        <>
                          <td>{order.quantity}</td>
                          <td>{formatCurrency(order.total_price)}</td>
                        </>
                      )}
                      <td>
                        <span
                          className={`status-badge ${
                            order.status === "approved"
                              ? "order-status-approved"
                              : order.status === "pending"
                              ? "order-status-pending"
                              : "order-status-reject"
                          }`}
                        >
                          {order.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center">
              <p>No orders found for this status.</p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
